import { Calendar } from '@design-system/Calendar';
import { useEffect, useState } from 'react';
import { Button } from '@design-system/Button';
import { useIntroQuestionnaireMutation } from '@viome/viome-base/src/usecase/Home/HomeQuery';
import {
  useGetIntroQuestionnairesV2,
  useGetJourneyStateV2,
} from '@viome/viome-base/src/usecase/Home/HomeV2Query';
import dayjs from 'dayjs';
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { cn } from '@utils/styles';
import { LoadingPage } from '@screens/LoadingPage';
import { ReadOnlyLockIcon } from '@/components/icons/ReadOnlyLockIcon';
import { Loader } from '@design-system/Loader';
import { getBackgroundImageUrl } from '@utils/nutrition/getImageUrl';

export const CollectSamplePage = () => {
  const {
    data: journeyStateData,
    isLoading: isJourneyStateLoading,
    isError: isJourneyStateError
  } = useGetJourneyStateV2();
  const { data: introQuestionnaireData, isLoading } = useGetIntroQuestionnairesV2();
  const {isPending: answerAPILoading, mutate: submitAnswer} =
    useIntroQuestionnaireMutation();
  const navigate = useNavigate();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [questionnarieData, setQuestionnarieData] = useState<any>(null);
  const [date, setDate] = useState<Date | undefined>(undefined);

  const { questionnaries = [], kitId } = introQuestionnaireData || {};

  useEffect(() => {
    if (questionnaries.length > 0) {
      const currentQuestion = questionnaries[currentIndex];
      setQuestionnarieData(currentQuestion);
      const currentAnswer = currentQuestion?.answer;
      if (currentAnswer) {
        setDate(new Date(dayjs(currentAnswer).toDate()));
      } else {
        setDate(undefined);
      }
    }
  }, [questionnaries, currentIndex]);

  const progressValue = questionnaries.length > 0 ? ((currentIndex + 1) / questionnaries.length) * 100 : 0;
  const currentIndexAtLastPage = currentIndex === questionnaries.length - 1;
  const isReadOnly = journeyStateData?.is_questionaire_processed || false;

  const onClickNext = (skipQuestion: boolean) => {
    if (answerAPILoading || skipQuestion) {
      if (skipQuestion && currentIndexAtLastPage) {
        navigate(-1);
      } else if (skipQuestion && !currentIndexAtLastPage) {
        setCurrentIndex(currentIndex + 1);
      }
      return;
    }
    if (isReadOnly) {
      if (currentIndexAtLastPage) {
        navigate(-1);
        return;
      }
      setCurrentIndex(currentIndex + 1);
      return;
    }
    if (currentIndex < (questionnaries.length - 1) || currentIndexAtLastPage) {
      submitAnswer(
        {
          answers: [
            {
              questionId: questionnarieData.question_id,
              answer: dayjs(date).format('YYYY-MM-DD'),
            },
          ],
          kitId: kitId ?? '',
          testId: questionnarieData.test_id,
        },
        {
          onSuccess: () => {
            if (currentIndexAtLastPage) {
              navigate(-1);
            } else {
              setCurrentIndex(currentIndex + 1);
            }
          },
        },
      );
    }
  };

  const onClickCollectLater = () => {
    setDate(undefined);
    onClickNext(true);
  }

  const onChangeDates = (newDate: Date | undefined) => {
    setDate(newDate || undefined);
  };

  const currentQuestionDateFromAPI = questionnarieData?.answer && dayjs(questionnarieData?.answer).format('MM/DD/YYYY');
  const changedQuestionDate = dayjs(date).format('MM/DD/YYYY')

  const updateOrNextButtonText = (currentQuestionDateFromAPI !== changedQuestionDate) ? 'Update' : 'Next';

  const onClickBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      navigate(-1);
    }
  };

  if (isJourneyStateLoading || isLoading) {
    return <LoadingPage />
  }

  if (isJourneyStateError || !introQuestionnaireData) {
    return <ErrorLoadingPage />
  }

  {/* Replace the below URL with S3 */}
  return (
    <div
      className="h-full flex flex-col"
      style={{
        backgroundImage: `url(${getBackgroundImageUrl('collect_sample_background.png')})`,
        backgroundSize: 'cover',
      }}
      data-testid="collectSample-page"
    >
      <div className="p-6 space-y-4 page-container" data-testid="collectSample-header">
        <div className="flex items-center justify-between" data-testid="collectSample-navigation">
          <Button
            variant='ghost'
            className="mt-6 flex items-center gap-2 text-secondary"
            onClick={onClickBack}
            data-testid="collectSample-backButton"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </Button>
        </div>

        <div className="w-full h-1 rounded-full overflow-hidden bg-stroke-secondary-subtle" data-testid="collectSample-progressContainer">
          <div
            className="h-full bg-background-success transition-all duration-300 ease-in-out"
            style={{ width: `${progressValue}%` }}
            data-testid="collectSample-progressBar"
          />
        </div>
      </div>

      <div className="p-6 space-y-4 page-container" data-testid="collectSample-content">
        <div
          className={cn(
            "typography-d2 flex items-center my-12",
            !isReadOnly && 'justify-center'
          )}
          data-testid="collectSample-questionTitle"
        >
          {questionnarieData?.question_title}
        </div>
        {isReadOnly && (
          <div className='flex' data-testid="collectSample-readOnlyIndicator">
            <ReadOnlyLockIcon />
            <div className='font-sans typography-body text-secondary ml-2'>
              Read only
            </div>
          </div>
        )}
        <Calendar
          mode="single"
          captionLayout="dropdown"
          selected={date}
          onSelect={onChangeDates}
          fromDate={questionnarieData?.minDate}
          toDate={questionnarieData?.maxDate}
          month={date}
          disabled={isReadOnly}
        />
      </div>

      <div
        className={cn(
          'flex items-center p-6 page-container',
          isReadOnly ? 'justify-end' : 'justify-between'
        )}
        data-testid="collectSample-actions">
        {!isReadOnly && (
          <Button
            variant="ghost"
            className="text-secondary"
            disabled={answerAPILoading}
            onClick={onClickCollectLater}
            data-testid="collectSample-collectLaterButton"
          >
            I will collect later
          </Button>
        )}
        {answerAPILoading ? (
          // inline styles to override the existing styles and place the loader at required place
          <Loader style={{ inset: 'unset !important', position: 'static' }} />
        ) : (
          <Button
            variant="default"
            className="px-10 py-3"
            onClick={() => onClickNext(false)}
            disabled={answerAPILoading}
            data-testid="collectSample-nextButton"
          >
            {currentIndexAtLastPage ? 'Done' : updateOrNextButtonText}
          </Button>
        )}
      </div>
    </div>
  );
};
